import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MoodThemeContext = createContext(null);

// Mood-based color schemes
export const MOOD_THEMES = {
  very_happy: {
    name: "Euforia",
    primary: "#FFD700", // Gold
    secondary: "#FF8C00", // Dark Orange
    accent: "#FFA500", // Orange
    gradient: "from-yellow-400 via-orange-400 to-amber-300",
    bgGradient: "radial-gradient(ellipse at top, rgba(255, 215, 0, 0.15), transparent 50%)",
    particles: "✨",
    emoji: "😄",
    description: "¡Energía radiante!"
  },
  happy: {
    name: "Alegría",
    primary: "#00E5FF", // Cyan
    secondary: "#00BCD4", // Cyan darker
    accent: "#84FFFF", // Cyan light
    gradient: "from-cyan-400 via-sky-400 to-blue-400",
    bgGradient: "radial-gradient(ellipse at top, rgba(0, 229, 255, 0.12), transparent 50%)",
    particles: "💫",
    emoji: "🙂",
    description: "Todo fluye bien"
  },
  neutral: {
    name: "Equilibrio",
    primary: "#9C27B0", // Purple
    secondary: "#7B1FA2", // Purple darker
    accent: "#E1BEE7", // Purple light
    gradient: "from-purple-500 via-violet-500 to-indigo-500",
    bgGradient: "radial-gradient(ellipse at top, rgba(156, 39, 176, 0.1), transparent 50%)",
    particles: "🌟",
    emoji: "😐",
    description: "Momento de reflexión"
  },
  sad: {
    name: "Melancolía",
    primary: "#3F51B5", // Indigo
    secondary: "#303F9F", // Indigo darker
    accent: "#C5CAE9", // Indigo light
    gradient: "from-indigo-500 via-blue-600 to-slate-600",
    bgGradient: "radial-gradient(ellipse at bottom, rgba(63, 81, 181, 0.15), transparent 50%)",
    particles: "🌧️",
    emoji: "😔",
    description: "Es válido sentir así"
  },
  very_sad: {
    name: "Tormenta",
    primary: "#455A64", // Blue Grey
    secondary: "#37474F", // Blue Grey darker
    accent: "#90A4AE", // Blue Grey light
    gradient: "from-slate-600 via-gray-600 to-zinc-700",
    bgGradient: "radial-gradient(ellipse at bottom, rgba(69, 90, 100, 0.2), transparent 50%)",
    particles: "💧",
    emoji: "😢",
    description: "Esto también pasará"
  },
  angry: {
    name: "Fuego",
    primary: "#F44336", // Red
    secondary: "#D32F2F", // Red darker
    accent: "#FFCDD2", // Red light
    gradient: "from-red-500 via-orange-600 to-red-700",
    bgGradient: "radial-gradient(ellipse at center, rgba(244, 67, 54, 0.15), transparent 50%)",
    particles: "🔥",
    emoji: "😡",
    description: "Respira profundo"
  },
  anxious: {
    name: "Vórtice",
    primary: "#9575CD", // Deep Purple light
    secondary: "#7E57C2", // Deep Purple
    accent: "#D1C4E9", // Deep Purple lighter
    gradient: "from-violet-500 via-purple-500 to-fuchsia-500",
    bgGradient: "radial-gradient(ellipse at center, rgba(149, 117, 205, 0.15), transparent 50%)",
    particles: "🌀",
    emoji: "😰",
    description: "Un paso a la vez"
  },
  tired: {
    name: "Luna",
    primary: "#607D8B", // Blue Grey
    secondary: "#546E7A", // Blue Grey darker
    accent: "#B0BEC5", // Blue Grey light
    gradient: "from-slate-500 via-gray-500 to-zinc-500",
    bgGradient: "radial-gradient(ellipse at bottom, rgba(96, 125, 139, 0.15), transparent 50%)",
    particles: "🌙",
    emoji: "😴",
    description: "Descansa cuando puedas"
  },
  in_love: {
    name: "Amor",
    primary: "#E91E63", // Pink
    secondary: "#C2185B", // Pink darker
    accent: "#F8BBD9", // Pink light
    gradient: "from-pink-400 via-rose-500 to-red-400",
    bgGradient: "radial-gradient(ellipse at top, rgba(233, 30, 99, 0.12), transparent 50%)",
    particles: "💖",
    emoji: "😍",
    description: "El amor ilumina"
  },
  sick: {
    name: "Recuperación",
    primary: "#4CAF50", // Green
    secondary: "#388E3C", // Green darker
    accent: "#C8E6C9", // Green light
    gradient: "from-green-500 via-emerald-500 to-teal-500",
    bgGradient: "radial-gradient(ellipse at center, rgba(76, 175, 80, 0.1), transparent 50%)",
    particles: "🍵",
    emoji: "🤒",
    description: "Cuídate mucho"
  },
  surprised: {
    name: "Asombro",
    primary: "#FF5722", // Deep Orange
    secondary: "#E64A19", // Deep Orange darker
    accent: "#FFCCBC", // Deep Orange light
    gradient: "from-orange-400 via-amber-500 to-yellow-400",
    bgGradient: "radial-gradient(ellipse at top right, rgba(255, 87, 34, 0.15), transparent 50%)",
    particles: "⚡",
    emoji: "😲",
    description: "¡La vida sorprende!"
  },
  relaxed: {
    name: "Serenidad",
    primary: "#26A69A", // Teal
    secondary: "#00897B", // Teal darker
    accent: "#B2DFDB", // Teal light
    gradient: "from-teal-400 via-cyan-500 to-emerald-400",
    bgGradient: "radial-gradient(ellipse at center, rgba(38, 166, 154, 0.12), transparent 50%)",
    particles: "🍃",
    emoji: "😌",
    description: "Paz interior"
  },
  sporty: {
    name: "Energía",
    primary: "#FF9800", // Orange
    secondary: "#F57C00", // Orange darker
    accent: "#FFE0B2", // Orange light
    gradient: "from-orange-400 via-yellow-500 to-lime-400",
    bgGradient: "radial-gradient(ellipse at top, rgba(255, 152, 0, 0.15), transparent 50%)",
    particles: "💪",
    emoji: "💪",
    description: "¡A por todas!"
  },
  forgiven: {
    name: "Paz",
    primary: "#9CCC65", // Light Green
    secondary: "#7CB342", // Light Green darker
    accent: "#DCEDC8", // Light Green light
    gradient: "from-lime-400 via-green-400 to-emerald-400",
    bgGradient: "radial-gradient(ellipse at center, rgba(156, 204, 101, 0.1), transparent 50%)",
    particles: "🕊️",
    emoji: "🙏",
    description: "Libertad interior"
  }
};

// Default theme when no mood is selected
const DEFAULT_THEME = {
  name: "MoodPunk",
  primary: "#00F3FF", // Neon cyan
  secondary: "#A855F7", // Neon purple
  accent: "#FFD700", // Gold
  gradient: "from-neon-cyan via-neon-purple to-neon-cyan",
  bgGradient: "radial-gradient(ellipse at top, rgba(168, 85, 247, 0.1), transparent 50%), radial-gradient(ellipse at bottom right, rgba(0, 243, 255, 0.05), transparent 50%)",
  particles: "⚡",
  emoji: "⚡",
  description: "Rastrea tus emociones"
};

const MOOD_THEME_KEY = "moodpunk_mood_theme";

export const useMoodTheme = () => {
  const context = useContext(MoodThemeContext);
  if (!context) {
    throw new Error("useMoodTheme must be used within a MoodThemeProvider");
  }
  return context;
};

export const MoodThemeProvider = ({ children }) => {
  const [currentMood, setCurrentMood] = useState(null);
  const [dynamicThemeEnabled, setDynamicThemeEnabled] = useState(() => {
    const saved = localStorage.getItem("moodpunk_dynamic_theme");
    return saved !== "false"; // Default to true
  });

  // Get the current mood theme
  const moodTheme = currentMood && dynamicThemeEnabled 
    ? MOOD_THEMES[currentMood] || DEFAULT_THEME
    : DEFAULT_THEME;

  // Fetch the most recent mood to set the theme
  const syncMoodTheme = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/moods?limit=1`);
      if (response.data.length > 0) {
        const latestMood = response.data[0].mood;
        setCurrentMood(latestMood);
      }
    } catch (error) {
      console.error("Error fetching mood for theme:", error);
    }
  }, []);

  // Update mood theme
  const setMoodTheme = useCallback((mood) => {
    setCurrentMood(mood);
    localStorage.setItem(MOOD_THEME_KEY, mood);
  }, []);

  // Toggle dynamic theme
  const toggleDynamicTheme = useCallback(() => {
    setDynamicThemeEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem("moodpunk_dynamic_theme", newValue.toString());
      return newValue;
    });
  }, []);

  // Apply CSS variables when mood changes
  useEffect(() => {
    if (dynamicThemeEnabled && moodTheme) {
      document.documentElement.style.setProperty('--mood-primary', moodTheme.primary);
      document.documentElement.style.setProperty('--mood-secondary', moodTheme.secondary);
      document.documentElement.style.setProperty('--mood-accent', moodTheme.accent);
    } else {
      document.documentElement.style.setProperty('--mood-primary', DEFAULT_THEME.primary);
      document.documentElement.style.setProperty('--mood-secondary', DEFAULT_THEME.secondary);
      document.documentElement.style.setProperty('--mood-accent', DEFAULT_THEME.accent);
    }
  }, [moodTheme, dynamicThemeEnabled]);

  // Initial sync on mount
  useEffect(() => {
    syncMoodTheme();
  }, [syncMoodTheme]);

  const value = {
    currentMood,
    moodTheme,
    setMoodTheme,
    syncMoodTheme,
    dynamicThemeEnabled,
    toggleDynamicTheme,
    allThemes: MOOD_THEMES,
    defaultTheme: DEFAULT_THEME
  };

  return (
    <MoodThemeContext.Provider value={value}>
      {children}
    </MoodThemeContext.Provider>
  );
};

export default MoodThemeContext;
